import { Constants } from '@pwc/pwc-deals-web-common';
import cloneDeep from 'lodash/cloneDeep';
import {
  getExcelLibraryItemsByGroup,
  getPptLibraryItemsByGroup,
  getRichTextLibraryItemsByGroup,
} from '../contentLibrary/libraryItemService';
import {
  formatTypeMap,
  typeToName,
  upperCaseNames,
} from '../project/ViewProject/ProjectOverview/utils/constants';

export const initialSections = [
  {
    order: 1,
    layoutType: Constants.SECTION_LAYOUT_TYPE.ONE_COLUMN,
    items: [],
  },
];

function capitalizeFirstLetter(a) {
  return a.charAt(0).toUpperCase() + a.slice(1);
}

const fixLowerCaseToUpperCase = (x, names) => {
  const changed = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(x)) {
    changed[names.includes(key) ? capitalizeFirstLetter(key) : key] = value;
  }

  return changed;
};

export const prepareLibraryItems = async ({
  projectId,
  libraryItemGroups,
  contextStatusInfoItem,
  excelLibraryItemsCallback,
  isPwcUser,
}) => {
  const fetchRichTextItems = async (richTextLibraryItemList) => {
    const richTextLibItems = richTextLibraryItemList
      .reduce((accum, item) => {
        const { libraryItemId: itemId } = item;
        const parsedId = Number.isInteger(parseInt(itemId, 10))
          ? parseInt(itemId, 10)
          : null;
        return accum.concat([parsedId]);
      }, [])
      .filter((item) => item !== null);
    const reqBody = {
      libraryItemIds: richTextLibItems,
    };
    try {
      const response = await getRichTextLibraryItemsByGroup(
        projectId,
        reqBody,
        contextStatusInfoItem,
        isPwcUser
      );
      const { richTextLibraryItems } = response;

      return richTextLibraryItems;
    } catch (e) {
      return richTextLibraryItemList.map((l) => ({
        ...l,
        id: Number(l.libraryItemId),
        column: null,
        richTextContent: {
          richTextData: null,
        },
        hasError: true,
        type: -1,
      }));
    }
  };

  const transformExcelPptGroupResponse = (response) => {
    const { fileContents } = response;
    return fileContents.map((item) => {
      const { data = {} } = item.autoSync || {};

      return {
        ...item,
        id: item.id,
        name: item.name,
        type: item.type,
        content: item.value,
        autoSync: item.autoSync,
        groupedByKey: response.groupedByKey,
        fileName: response.fileName,
        worksheetName: item.worksheetName || data.worksheetName,
        groupName: item.groupName || data.groupName,
        fileContents,
      };
    });
  };

  const fetchPptItems = async (excelPptLibraryItemList) => {
    const results = [];

    // we need to send back exactly the same list of items that we were asked to load
    // it is in order to be able to show "Unable to load" should the fetch fail
    // eslint-disable-next-line no-restricted-syntax
    for (const group of excelPptLibraryItemList) {
      const reqBody = {
        groupedByKey: group.groupedByKey,
        sharePointItemUrl: group.sharePointItemUrl,
        driveId: group.driveId,
        driveItemId: group.driveItemId,
        siteId: group.siteId,
      };
      const pptLibraryItems = group.excelPptLibraryItems
        .slice()
        .filter(
          (i) => i.type === Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value
        );
      reqBody.pptLibraryItems = pptLibraryItems.map((libItem) => {
        const cloned = cloneDeep(libItem);
        let { data } = cloned;
        data.formatType = formatTypeMap[libItem.type];
        data[typeToName[libItem.type]] = data.name || data.groupName;
        data = fixLowerCaseToUpperCase(data, upperCaseNames[libItem.type]);

        return { ...cloned, data };
      });
      if (reqBody.pptLibraryItems.length) {
        try {
          const result = transformExcelPptGroupResponse(
            // eslint-disable-next-line no-await-in-loop
            await getPptLibraryItemsByGroup(projectId, reqBody)
          );
          results.push(result);
        } catch (err) {
          // the request failed, we do not have the HTML/SVG content
          // but we need to signal that we tried to load the item
          // so that the "Unable to load content" component is displayed
          results.push(
            pptLibraryItems.map((x) => {
              const data = {
                sharePointItemUrl: reqBody.sharePointItemUrl,
                ...x.data,
              };

              return {
                ...x,
                data,
                hasError: true,
                type: -1,
                error: {
                  // components depends on type -1 but the original data of what type is lost, so type is inserted here for UI purposes
                  ...err,
                  data,
                  type: x.type,
                },
              };
            })
          );
        }
      }
    }
    return results.flat();
  };

  const fetchExcelItems = async (excelPptLibraryItemList) => {
    const results = [];

    // we need to send back exactly the same list of items that we were asked to load
    // it is in order to be able to show "Unable to load" should the fetch fail
    // eslint-disable-next-line no-restricted-syntax
    for (const group of excelPptLibraryItemList) {
      const reqBody = {
        groupedByKey: group.groupedByKey,
        sharePointItemUrl: group.sharePointItemUrl,
        driveId: group.driveId,
        driveItemId: group.driveItemId,
        siteId: group.siteId,
      };
      const excelLibraryItems = group.excelPptLibraryItems
        .slice()
        .filter(
          (i) => i.type !== Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value
        );

      if (excelLibraryItems?.length > 0) {
        reqBody.excelLibraryItems = [];
      }

      for (let i = 0; i < excelLibraryItems?.length; i += 1) {
        const libItem = excelLibraryItems[i];
        const cloned = cloneDeep(libItem);
        const { data } = cloned;
        data.formatType = formatTypeMap[libItem.type];
        data[typeToName[libItem.type]] = data.name || data.groupName;
        delete data.Name;
        delete data.name;
        delete cloned.childrenData;

        const fixedCaseLetters = fixLowerCaseToUpperCase(
          data,
          upperCaseNames[libItem.type]
        );

        reqBody.excelLibraryItems.push({
          ...cloned,
          data: fixedCaseLetters,
        });
        if (
          [
            Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value,
            Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value,
          ].includes(libItem.type)
        ) {
          const fixedSVGCaseLetters = fixLowerCaseToUpperCase(
            { ...data, formatType: 'SVG' },
            upperCaseNames[libItem.type]
          );
          reqBody.excelLibraryItems.push({
            ...cloned,
            data: fixedSVGCaseLetters,
          });
        }
      }
      if (reqBody.excelLibraryItems?.length) {
        try {
          const result = transformExcelPptGroupResponse(
            // eslint-disable-next-line no-await-in-loop
            await getExcelLibraryItemsByGroup(projectId, reqBody)
          );
          if (excelLibraryItemsCallback) {
            excelLibraryItemsCallback(result.find(Boolean).fileContents);
          }
          results.push(result);
        } catch (err) {
          // the request failed, we do not have the HTML/SVG content
          // but we need to signal that we tried to load the item
          // so that the "Unable to load content" component is displayed
          results.push(
            excelLibraryItems.map((x) => {
              const data = {
                sharePointItemUrl: reqBody.sharePointItemUrl,
                ...x.data,
              };

              return {
                ...x,
                data,
                hasError: true,
                type: -1,
                error: {
                  // components depends on type -1 but the original data of what type is lost, so type is inserted here for UI purposes
                  ...err,
                  data,
                  type: x.type,
                },
              };
            })
          );
        }
      }
    }
    return results.flat();
  };

  const {
    excelPptLibraryItemList = [],
    richTextLibraryItemList = [],
    powerBiLibraryItemList = [],
    tableauLibraryItemList = [],
    dynamicExcelLibraryItemList = [],
  } = libraryItemGroups || {};

  let richTextData;
  let excelData;
  let pptData;
  const powerBiData = powerBiLibraryItemList.flatMap((x) =>
    x.powerBiLibraryItems.map((y) => ({ ...y, reportId: x.reportId }))
  );
  const tableauData = tableauLibraryItemList.flatMap((x) =>
    x.tableauLibraryItems.map((y) => ({ ...y, reportId: x.reportId }))
  );
  const dynamicExcelData = dynamicExcelLibraryItemList.flatMap((x) =>
    x.dynamicExcelLibraryItems.map((y) => {
      return { ...y, sharePointItemUrl: x.sharePointItemUrl };
    })
  );

  if (excelPptLibraryItemList && excelPptLibraryItemList.length > 0) {
    excelData = await fetchExcelItems(excelPptLibraryItemList);
    pptData = await fetchPptItems(excelPptLibraryItemList);
  }
  if (richTextLibraryItemList && richTextLibraryItemList.length > 0) {
    richTextData = await fetchRichTextItems(richTextLibraryItemList, isPwcUser);
  }
  const excelLibraryItems = excelData || [];
  const pptLibraryItems = pptData || [];
  const richTextLibraryItems = richTextData || [];
  const requests = excelPptLibraryItemList
    .map((x) => x.excelPptLibraryItems)
    .reduce((a, b) => a.concat(b), []);

  return {
    excelLibraryItems,
    pptLibraryItems,
    richTextLibraryItems,
    requests,
    powerBiData,
    tableauData,
    dynamicExcelData,
  };
};

export const loadLibraryItems = ({
  sections,
  sharedLibraryItems,
  excelLibraryItems,
  richTextLibraryItems,
  requests,
  pptLibraryItems,
  powerBiData,
  tableauData,
  dynamicExcelData,
  caller,
}) => {
  if (!richTextLibraryItems) {
    throw new Error(
      `No items supplied, call prepareLibraryItems first in ${caller}`
    );
  }

  const sectionLibraryItemIds = [];
  const newSections = [];
  if (sections) {
    sections.filter(Boolean).forEach((section) => {
      const items = [];
      (section.items || []).forEach((item) => {
        sectionLibraryItemIds.push(item.libraryItemId);

        const sharedLibraryItem =
          sharedLibraryItems &&
          sharedLibraryItems.find((x) => x.id === item.libraryItemId);

        const keepDataTypes = [
          Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value,
          Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT.value,
          Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT_PAGE.value,
        ];

        const sharedRTEchildData =
          sharedLibraryItems &&
          sharedLibraryItems.filter((x) => !keepDataTypes.includes(x.type));

        if (sharedLibraryItem) {
          items.push({
            ...item,
            ...sharedLibraryItem,
            data: keepDataTypes.includes(sharedLibraryItem.type)
              ? sharedLibraryItem.data
              : undefined,
            analysisColumn: item.column,
            value: sharedLibraryItem.data,
            content: sharedLibraryItem.data,
            richTextContent: { richTextData: sharedLibraryItem.data },
            childrenData: sharedRTEchildData,
          });
        }

        const richTextLibraryItem = richTextLibraryItems.find(
          (i) => i.id === item.libraryItemId
        );
        if (richTextLibraryItem) {
          items.push({
            ...item,
            ...richTextLibraryItem,
            analysisColumn: item.column,
            data: richTextLibraryItem.richTextContent.richTextData,
          });
        }
        const excelLibrarySvgHtmlItems = excelLibraryItems.filter(
          (i) => i.id === item.libraryItemId
        );
        if (excelLibrarySvgHtmlItems && excelLibrarySvgHtmlItems.length > 0) {
          const request = requests.find((i) => i.id === item.libraryItemId);
          let excelLibraryItem;
          if (
            [
              Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value,
              Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value,
            ].includes(request.type)
          ) {
            excelLibraryItem = excelLibrarySvgHtmlItems.find(
              (i) =>
                i.id === item.libraryItemId &&
                (i.formatType === request.data.formatType ||
                  Boolean(i.hasError || i.error))
            );
          } else {
            excelLibraryItem = excelLibrarySvgHtmlItems.find(
              (i) => i.id === item.libraryItemId
            );
          }
          items.push({
            ...request,
            ...item,
            data: JSON.stringify({ ...item.data, ...request.data }),
            ...excelLibraryItem,
            analysisColumn: item.column,
          });
        }
        const pptLibraryItem = pptLibraryItems.find(
          (i) => i.id === item.libraryItemId
        );
        if (pptLibraryItem) {
          const request = requests.find((i) => i.id === item.libraryItemId);
          items.push({
            ...request,
            data: JSON.stringify({ ...item.data, ...request.data }),
            ...item,
            ...pptLibraryItem,
            analysisColumn: item.column,
          });
        }
        const powerBiItem = powerBiData.find(
          (i) => i.id === item.libraryItemId
        );
        if (powerBiItem) {
          items.push({
            ...powerBiItem,
            ...item,
            data: JSON.stringify(powerBiItem.data),
            reportId: powerBiItem.data.ReportId || powerBiItem.reportId,
            pageName: powerBiItem.data.PageName || powerBiItem.data.pageName,
            analysisColumn: item.column,
          });
        }
        const tableauItem = tableauData.find(
          (i) => i.id === item.libraryItemId
        );
        if (tableauItem) {
          items.push({
            ...tableauItem,
            ...item,
            data: JSON.stringify(tableauItem.data),
            reportId: tableauItem.data.ReportId || tableauItem.reportId,
            pageName: tableauItem.data.PageName || tableauItem.data.pageName,
            analysisColumn: item.column,
          });
        }
        const dynamicExcelItem = dynamicExcelData.find(
          (i) => i.id === item.libraryItemId
        );
        if (dynamicExcelItem) {
          items.push({
            ...dynamicExcelItem,
            ...item,
            analysisColumn: item.column,
          });
        }
      });
      newSections.push({ ...section, items });
    });

    // sectionLibraryItemIds have all the ids of directly used library items in sections
    // the rest of excelLibraryItems and pptLibraryItems are RTE library items
    excelLibraryItems
      .filter((i) => !sectionLibraryItemIds.includes(i.id))
      .forEach((excelLibraryItem) => {
        const request = requests.find((i) => excelLibraryItem.id === i.id);

        if (request) {
          // eslint-disable-next-line no-underscore-dangle
          window.__LIBRARYITEMSTORE__.add({
            ...request,
            data: JSON.stringify({ ...excelLibraryItem.data, ...request.data }),
            ...excelLibraryItem,
          });
        }
      });

    pptLibraryItems
      .filter((i) => !sectionLibraryItemIds.includes(i.id))
      .forEach((pptLibraryItem) => {
        const request = requests.find((i) => i.id === pptLibraryItem.id);
        // eslint-disable-next-line no-underscore-dangle
        window.__LIBRARYITEMSTORE__.add({
          ...request,
          data: JSON.stringify({ ...pptLibraryItem.data, ...request.data }),
          ...pptLibraryItem,
        });
      });

    (sharedLibraryItems || [])
      .filter(Boolean)
      .filter((i) => !sectionLibraryItemIds.includes(i.id))
      .forEach((sharedLibraryItem) =>
        // eslint-disable-next-line no-underscore-dangle
        window.__LIBRARYITEMSTORE__.add({
          ...sharedLibraryItem,
          data:
            sharedLibraryItem.type ===
            Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value
              ? sharedLibraryItem.data
              : undefined,
          value: sharedLibraryItem.data,
          content: sharedLibraryItem.data,
          rendered: sharedLibraryItem.data,
          richTextContent: { richTextData: sharedLibraryItem.data },
        })
      );
  }
  return newSections;
};
